import React from "react"
import { Box, Button, List } from "@mui/material"
import { useKBar } from "kbar"
import {
  HiOutlineLink,
  HiOutlineUserCircle,
  HiOutlineCog,
  HiOutlineKey,
  HiOutlineHome,
  HiOutlineCollection,
  HiHome,
  HiOutlineFingerPrint,
  HiCollection,
  HiKey,
  HiCog,
  HiUserCircle,
  HiLink,
  HiOutlineSearch,
  HiGlobe,
  HiOutlineGlobe,
  HiClipboardList,
  HiOutlineClipboardList,
  HiOutlineChartSquareBar,
  HiChartSquareBar,
} from "react-icons/hi"
import Link from "next/link"

import SidebarItem from "components/SidebarItem"
import Logo from "components/Logo"
import PlanGate, { hasPlanFeatures } from "components/PlanGate"
import { hasPermission } from "components/PermissionsGate"
import { useCurrentUser } from "context/api"
import { roles } from "util/constants"

export default function DashboardSidebar({ ...props }) {
  const { data: currentUser } = useCurrentUser()
  const { query: kbarActions } = useKBar()

  const hasBi = hasPlanFeatures({
    user: currentUser,
    allowedFeatures: ["businessIntelligence"],
  })
  const hasDemandPlanning = hasPlanFeatures({
    user: currentUser,
    allowedFeatures: ["demandPlanning"],
  })
  const hasAttribution = hasPlanFeatures({
    user: currentUser,
    allowedFeatures: ["pixelAttribution"],
  })
  const hasCompetitorData = hasPlanFeatures({
    user: currentUser,
    allowedFeatures: ["addRemoveCompetitors"],
  })
  const hasMarketInsights = hasPlanFeatures({
    user: currentUser,
    allowedFeatures: ["marketInsights"],
  })

  return (
    <Box
      sx={{
        position: "fixed",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100vh",
        width: 250,
        bgcolor: "warmGray.100",
        borderRight: 1,
        borderColor: "warmGray.200",
        displayPrint: "none",
        ...props.sx,
      }}
    >
      <Box
        sx={{ px: 1.5, pt: 1.5 }}
        ml={1.5}
        my={1}
        display="flex"
        alignItems="center"
        gap={1}
      >
        <Box
          component={Link}
          href={{ pathname: `/dashboard` }}
          passHref
          sx={{ cursor: "pointer" }}
        >
          <a>
            <Logo width={54} />
          </a>
        </Box>
        <Box
          sx={{
            px: 0.75,
            py: 0.2,
            bgcolor: "primary.50",
            color: "primary.600",
            borderRadius: 2,
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: "9px",
            transform: "translateY(1px)",
            border: 1,
            borderColor: "primary.400",
          }}
        >
          Beta
        </Box>
      </Box>
      <List sx={{ pt: 0.5, pb: 0 }}>
        <Box
          component="li"
          sx={{
            width: "100%",
            display: "flex",
            py: 0,
            px: 1.5,
            mb: 1.5,
          }}
        >
          <Button
            onClick={kbarActions.toggle}
            startIcon={<HiOutlineSearch size={18} />}
            endIcon={<>⌘K</>}
            // @ts-ignore
            color="gray"
            variant="outlined"
            sx={{
              width: "100%",
              justifyContent: "flex-start",
              px: 1.5,
              py: 0.25,
              borderColor: "gray.200",
              bgcolor: "gray.50",
              color: "gray.400",
              borderRadius: 99,
              ".MuiButton-endIcon": {
                color: "gray.400",
                fontSize: "xs",
                fontWeight: "medium",
                position: "absolute",
                right: 16,
              },
            }}
          >
            Quick Find
          </Button>
        </Box>
        <SidebarItem
          href={{ pathname: "/dashboard" }}
          title="Home"
          icon={<HiHome size={18} />}
          outlineIcon={<HiOutlineHome size={18} />}
          activePaths={[
            {
              path: "/dashboard",
              strategy: "exact",
            },
          ]}
        />
        <SidebarItem
          href={{ pathname: "/dashboard/store/overview" }}
          title="Website Analytics"
          icon={<HiChartSquareBar size={18} />}
          outlineIcon={<HiOutlineChartSquareBar size={18} />}
          activePaths={[
            {
              path: "/dashboard/store/overview",
              strategy: "exact",
            },
            {
              path: "/dashboard/store/ltv-cac",
              strategy: "exact",
            },
          ]}
          deemphasized={!hasBi}
        />
        <SidebarItem
          href={{ pathname: "/dashboard/attribution/overview" }}
          title="Attribution"
          icon={<HiOutlineFingerPrint size={18} />}
          outlineIcon={<HiOutlineFingerPrint size={18} />}
          activePaths={[
            {
              path: "/dashboard/attribution",
              strategy: "startsWith",
            },
          ]}
          deemphasized={!hasAttribution}
        />
        <SidebarItem
          href={{
            pathname: "/dashboard/companies/overview",
            activePathPattern: "/dashboard/companies",
          }}
          title="Competitor Tracking"
          icon={<HiCollection size={18} />}
          outlineIcon={<HiOutlineCollection size={18} />}
          activePaths={[
            {
              path: "/dashboard/companies/overview",
              strategy: "exact",
            },
            {
              path: "/dashboard/companies/products",
              strategy: "exact",
            },
            {
              path: "/dashboard/companies/[id]/",
              strategy: "startsWith",
            },
          ]}
          deemphasized={!hasCompetitorData}
        >
          <SidebarItem
            href={{ pathname: "/dashboard/companies/search" }}
            title="Explore"
            activePaths={[
              {
                path: "/dashboard/companies/search",
                strategy: "exact",
              },
            ]}
          />
          <SidebarItem
            href={{ pathname: "/dashboard/companies/benchmarking/companies" }}
            title="Benchmarking"
            activePaths={[
              {
                path: "/dashboard/companies/benchmarking",
                strategy: "startsWith",
              },
            ]}
            lastInSubmenu={true}
          />
        </SidebarItem>
        <SidebarItem
          href={{ pathname: "/dashboard/market-insights" }}
          title="Market Insights"
          icon={<HiGlobe />}
          outlineIcon={<HiOutlineGlobe />}
          deemphasized={!hasMarketInsights}
        />
        <SidebarItem
          href={{ pathname: "/dashboard/demand-planning" }}
          title="Inventory Management"
          icon={<HiClipboardList />}
          outlineIcon={<HiOutlineClipboardList />}
          deemphasized={!hasDemandPlanning}
        />
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Box>
        <SidebarItem
          href={{ pathname: "/dashboard/integrations" }}
          title="Integrations"
          icon={<HiLink />}
          outlineIcon={<HiOutlineLink />}
        />
        <Box
          sx={{
            mt: 1,
            py: 1,
            borderTopWidth: 1,
            borderTopColor: "gray.200",
            borderTopStyle: "solid",
          }}
        >
          <PlanGate allowedFeatures={["internalTools"]}>
            <SidebarItem
              href={{ pathname: "/dashboard/internal" }}
              key="Internal"
              title="Internal"
              icon={<HiKey />}
              outlineIcon={<HiOutlineKey />}
            />
          </PlanGate>
          {hasPermission({
            role: currentUser?.type,
            allowedRoles: [roles.ADMIN, roles.OWNER],
          }) && (
            <SidebarItem
              href={{ pathname: "/dashboard/admin" }}
              key="Admin"
              title="Admin"
              icon={<HiCog />}
              outlineIcon={<HiOutlineCog />}
            />
          )}
          <SidebarItem
            href={{ pathname: "/dashboard/account" }}
            key="My Account"
            title="My Account"
            icon={<HiUserCircle />}
            outlineIcon={<HiOutlineUserCircle />}
          />
        </Box>
      </Box>
    </Box>
  )
}
